import React, { useState } from 'react';
import { CommitteeCard } from './CommitteeCard';
import { CommitteeModal } from './CommitteeModal';
import { SUMMIT_CONFIG, CommitteeInfo } from '../config/summitConfig';

interface CommitteeSectionProps {
  reducedMotion: boolean;
  onSelectCommitteeForRegistration?: (committeeName: string) => void;
}

export const CommitteeSection: React.FC<CommitteeSectionProps> = ({
  reducedMotion,
  onSelectCommitteeForRegistration,
}) => {
  const [activeCategory, setActiveCategory] = useState<string>('ALL');
  const [selectedCommittee, setSelectedCommittee] = useState<CommitteeInfo | null>(null);

  const categories = ['ALL', 'International', 'Indian Parliamentary', 'Crisis & Specialized', 'Auction'];

  const filteredCommittees = activeCategory === 'ALL'
    ? SUMMIT_CONFIG.committees
    : SUMMIT_CONFIG.committees.filter(c => c.category === activeCategory);

  const handlePreselect = (committeeName: string) => {
    setSelectedCommittee(null);
    if (onSelectCommitteeForRegistration) {
      onSelectCommitteeForRegistration(committeeName);
    }
  };

  return (
    <section 
      id="committees" 
      className="relative py-28 sm:py-36 bg-gradient-to-b from-deepblack via-[#141414] to-deepblack overflow-hidden perspective-stage border-t border-white/5"
    >
      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 relative z-10">
        
        {/* Section Pre-title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-[1px] bg-bronze" />
          <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
            THE COMMITTEES • 09 CHAMBERS
          </span>
          <div className="w-12 h-[1px] bg-bronze" />
        </div>

        {/* Section Heading & Subheading */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ivory">
              CHOOSE YOUR ARENA. <br />
              <span className="text-beige font-light italic">FIND YOUR VOICE.</span>
            </h2>
          </div>
          <div className="max-w-md">
            <p className="text-sm text-ivory/70 font-light leading-relaxed">
              Nine distinct chambers spanning multilateral security, economic warfare, parliamentary democracy, historical battlegrounds, institutional ethics, and auction warfare.
            </p>
            <a
              href="#matrices"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-beige hover:text-white transition-colors"
            >
              <span>View Country & Portfolio Matrices ↓</span>
            </a>
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-10 pb-4 border-b border-white/5">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-medium rounded-sm border transition-all ${
                activeCategory === cat
                  ? 'border-beige bg-beige text-deepblack font-semibold shadow-md'
                  : 'border-white/10 text-ivory/65 hover:border-bronze/40 hover:text-beige'
              }`}
              data-cursor-interactive
            >
              {cat === 'ALL' ? 'ALL 09 COMMITTEES' : cat}
            </button>
          ))}
        </div>

        {/* Committee Grid (All 9 committees - 3x3 layout) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCommittees.map((committee) => (
            <CommitteeCard
              key={committee.id}
              committee={committee}
              onExplore={(c) => setSelectedCommittee(c)}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>

        {/* Bottom Classical Meander */}
        <div className="mt-16 w-full h-2 greek-key-border opacity-30" />

      </div>

      {/* Committee Modal */}
      <CommitteeModal
        committee={selectedCommittee}
        onClose={() => setSelectedCommittee(null)}
        onSelectForRegistration={handlePreselect}
        reducedMotion={reducedMotion}
      />
    </section>
  );
};
